import { pubsub } from '@services';

/**
 * Suggests switching market/country when Shopify's browsing-context detection
 * (IP-based storefront geolocation) disagrees with the current localization,
 * and a matching country exists in the localization form.
 */
const COOKIE_NAME = 'bw_loc_geo_suggest';
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/** Query flag on region-picker destination URLs; must match Liquid (`bw_loc_suggest=1`). */
const REGION_PICKER_LOC_QUERY_KEY = 'bw_loc_suggest';
const REGION_PICKER_LOC_QUERY_VALUE = '1';

/** @returns {boolean} */
function hasDismissCookie() {
  return document.cookie.split(';').some((c) => c.trim().startsWith(`${COOKIE_NAME}=`));
}

function setDismissCookie() {
  document.cookie = `${COOKIE_NAME}=1; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
}

/**
 * Region picker links append `bw_loc_suggest=1`. On the destination page, set the dismiss cookie
 * and strip the param from the URL so the geo localization modal does not show for 30 days.
 */
function consumeRegionPickerLocSuggestQueryParam() {
  const params = new URLSearchParams(window.location.search);
  if (params.get(REGION_PICKER_LOC_QUERY_KEY) !== REGION_PICKER_LOC_QUERY_VALUE) return;

  setDismissCookie();

  params.delete(REGION_PICKER_LOC_QUERY_KEY);
  const queryString = params.toString();
  const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', nextUrl);
}

/**
 * @returns {{ currentCountryIso: string, excludedCountryIso: string } | null}
 */
function readConfig() {
  const configuration = window.localizationSuggest;
  if (!configuration || typeof configuration.currentCountryIso !== 'string') return null;
  return {
    currentCountryIso: configuration.currentCountryIso,
    excludedCountryIso:
      typeof configuration.excludedCountryIso === 'string' ? configuration.excludedCountryIso : '',
  };
}

/** @returns {Set<string>} uppercase ISO codes */
function getSelectableCountryCodes() {
  const countrySelect = document.querySelector('localization-form [data-localization-country]');
  if (!countrySelect) return new Set();
  const items = countrySelect.querySelectorAll('[data-select-list] [role="menuitem"][data-value]');
  return new Set(
    [...items].map((el) => String(el.dataset.value || '').toUpperCase()).filter(Boolean)
  );
}

/** @param {string} iso */
function countryOptionExists(iso) {
  return getSelectableCountryCodes().has(String(iso || '').toUpperCase());
}

/**
 * Updates the localization country content-select to match the suggested ISO (case-insensitive).
 * Dispatches `change` on the hidden input so `localization-form` filters languages.
 * Call after the localization dialog has opened so `localization-dialog:opened` GTM hooks
 * still see the original market country as the initial value.
 * @param {string} detectedUpper - uppercase ISO (e.g. NL)
 * @returns {boolean}
 */
function preselectLocalizationCountry(detectedUpper) {
  const countrySelect = document.querySelector('localization-form [data-localization-country]');
  if (!countrySelect) return false;

  const list = countrySelect.querySelector('[data-select-list]');
  const hiddenInput = countrySelect.querySelector('[data-select-input]');
  const button = countrySelect.querySelector('[data-select-button]');
  if (!list || !hiddenInput || !button) return false;

  const options = list.querySelectorAll('[role="menuitem"][data-value]');
  const target = [...options].find(
    (opt) => String(opt.dataset.value || '').toUpperCase() === detectedUpper
  );
  if (!target) return false;

  options.forEach((opt) => opt.removeAttribute('data-selected'));
  target.setAttribute('data-selected', '');
  hiddenInput.value = target.dataset.value ?? '';
  button.innerHTML = target.cloneNode(true).innerHTML;
  hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

/** @returns {string} */
function browsingContextSuggestionsUrl() {
  const shopify = window.Shopify;
  const root =
    shopify && shopify.routes && typeof shopify.routes.root === 'string'
      ? shopify.routes.root
      : '/';
  const path = 'browsing_context_suggestions.json?country[enabled]=true';
  return root.endsWith('/') ? `${root}${path}` : `${root}/${path}`;
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object';
}

/**
 * @param {unknown} data
 * @returns {string|null} uppercase country handle / ISO
 */
function detectedCountryFromSuggestionsPayload(data) {
  if (!isPlainObject(data)) return null;

  const detectedValues = /** @type {Record<string, unknown>} */ (data).detected_values;
  if (!isPlainObject(detectedValues)) return null;

  const country = /** @type {Record<string, unknown>} */ (detectedValues).country;
  if (!isPlainObject(country)) return null;

  const handle = country.handle;
  if (handle === undefined || handle === null || handle === '') return null;

  return String(handle).toUpperCase();
}

/** @returns {Promise<string|null>} uppercase country handle / ISO */
async function fetchDetectedCountryIso() {
  const response = await fetch(browsingContextSuggestionsUrl(), {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;

  const data = await response.json();
  return detectedCountryFromSuggestionsPayload(data);
}

/**
 * @param {{ currentCountryIso: string, excludedCountryIso: string }} config
 * @returns {boolean}
 */
function isExcludedDummyMarket(config) {
  const excluded = config.excludedCountryIso.trim().toLowerCase();
  if (!excluded) return false;
  return config.currentCountryIso.trim().toLowerCase() === excluded;
}

/** @param {{ openedBySuggest: boolean }} state */
function onGeoSuggestDialogClose(state) {
  if (!state.openedBySuggest) return;
  setDismissCookie();
  state.openedBySuggest = false;
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {string} currentCountryUpper
 * @param {{ openedBySuggest: boolean }} state
 */
async function tryOpenGeoSuggestDialog(dialog, currentCountryUpper, state) {
  try {
    if (dialog.open) return;

    const detected = await fetchDetectedCountryIso();
    if (!detected || detected === currentCountryUpper) return;
    if (!countryOptionExists(detected)) return;

    state.openedBySuggest = true;
    pubsub.publish('dialog:open', { id: 'localization-dialog' });
    if (!dialog.open) {
      state.openedBySuggest = false;
      return;
    }

    preselectLocalizationCountry(detected);
  } catch {
    // ignore network / parse errors
  }
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {string} currentCountryUpper
 */
function setupGeoSuggestAutoOpen(dialog, currentCountryUpper) {
  const state = { openedBySuggest: false };

  dialog.addEventListener('close', () => onGeoSuggestDialogClose(state));

  const form = dialog.querySelector('#localization-form');
  form?.addEventListener(
    'submit',
    () => {
      if (state.openedBySuggest) setDismissCookie();
    },
    { capture: true }
  );

  requestAnimationFrame(() => {
    void tryOpenGeoSuggestDialog(dialog, currentCountryUpper, state);
  });
}

function init() {
  if (window.Shopify?.designMode) return;
  //if there is no country select, return
  const countrySelect = document.querySelector('localization-form [data-localization-country]');
  if (!countrySelect) return;

  consumeRegionPickerLocSuggestQueryParam();

  const config = readConfig();
  if (!config) return;
  if (isExcludedDummyMarket(config)) return;
  if (hasDismissCookie()) return;

  const dialog = document.getElementById('localization-dialog');
  if (!dialog) return;

  setupGeoSuggestAutoOpen(dialog, config.currentCountryIso.trim().toUpperCase());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
